// src/components/lead/sections/LeadSummary.tsx
import type { ReactNode } from "react";
import { cardSurfaceClass, cardMutedTextClass } from "@/components/common/surfaceClasses";
import type { ExtractedLeadV2 } from "../../../types/lead";
import { normalizePhoneDigits } from "../../../lib/lead/phones";
import { LeadSummaryActions } from "./LeadSummaryActions";
import { LeadSummaryHeader } from "./LeadSummaryHeader";
import { NotesSection } from "./NotesSection";

export type LeadLike = {
  id: string;
  name: string;
  transcript: string;
  extracted: ExtractedLeadV2 | null;
  sent: boolean;
  lastUpdated: number;
  queued?: boolean;
  sendError?: string | null;
  isTest?: boolean;

  // Manager-edited fields that extraction should NOT overwrite
  lockedFields?: Partial<Record<keyof ExtractedLeadV2, true>>;

  origin_location_id?: string | null;
  origin_location_name?: string | null;
  owner_user_id?: string | null;
  created_at?: string | null;
};

export type MergeSuggestionLike = {
  reason: string;
  sourceId: string;
} | null;

type LeadSummaryProps = {
  currentLead: LeadLike;
  leads: LeadLike[];
  isToday: (ts: number) => boolean;

  locationDisplay: string;
  leadSourceDisplay: string;
  installAddressDisplay: string;
  lastUpdatedDisplay: string | null;

  timelineDisplay: string;
  leadStatusClass: string;
  leadStatusLabel: string;
  leadAgeDisplay: string;

  compactView: boolean;
  setCompactView: (v: boolean) => void;

  mergeSuggestion: MergeSuggestionLike;
  setMergeSuggestion: (v: MergeSuggestionLike) => void;
  mergeLeadIntoCurrent: (sourceId: string) => void;

  buddyTags: string[];
  ghlPayload: any | null;

  newTagInput: string;
  setNewTagInput: (v: string) => void;

  updateCurrentLeadField: (field: keyof ExtractedLeadV2, value: any) => void;

  validationIssues: string[];
  canSend: boolean;

  isSending: boolean;
  handleSendClick: (lead: LeadLike) => void;
  reextractCurrentLead: () => void;

  devMode: boolean;

  showGhlLocationMismatchHint?: boolean;
  workspaceLocationLabel?: string | null;
  leadProvenanceNode?: ReactNode;
  leadTransferSection?: ReactNode;
  submitAsDealSection?: ReactNode;
};

export function LeadSummary(props: LeadSummaryProps) {
  const {
    currentLead,
    leads,
    isToday,
    locationDisplay,
    leadSourceDisplay,
    installAddressDisplay,
    lastUpdatedDisplay,
    timelineDisplay,
    leadStatusClass,
    leadStatusLabel,
    leadAgeDisplay,
    compactView,
    setCompactView,
    mergeSuggestion,
    setMergeSuggestion,
    mergeLeadIntoCurrent,
    buddyTags,
    ghlPayload,
    newTagInput,
    setNewTagInput,
    updateCurrentLeadField,
    validationIssues,
    canSend,
    isSending,
    handleSendClick,
    reextractCurrentLead,
    devMode,
    showGhlLocationMismatchHint,
    workspaceLocationLabel,
    leadProvenanceNode,
    leadTransferSection,
    submitAsDealSection,
  } = props;

  if (!currentLead?.extracted) return null;

  const e = currentLead.extracted;

  // interest_tags are the editable/custom tags
  const interestRaw = Array.isArray(e.interest_tags) ? e.interest_tags : [];

  const interestNormalized = new Set(
    interestRaw.map((t) => t.trim().toLowerCase().replace(/\s+/g, "_"))
  );

  // System tags = everything coming from buildBuddyTags
  // that is NOT exactly one of the interest_tags.
  const systemTags = Array.from(
    new Set(buddyTags.filter((tag) => !interestNormalized.has(tag)))
  );

  // Editable tags = whatever is in interest_tags
  const editableTags = Array.from(
    new Set(interestRaw.map((t) => (t || "").trim()).filter(Boolean))
  );

  const addTag = () => {
    const raw = newTagInput.trim();
    if (!raw) return;

    const normalized = raw.toLowerCase().replace(/\s+/g, "_");
    const next = Array.from(new Set([...editableTags, normalized]));

    updateCurrentLeadField("interest_tags", next.length ? next : null);
    setNewTagInput("");
  };

  const idAsNumber = Number(currentLead.id);
  const createdAtMs = Number.isFinite(idAsNumber)
    ? idAsNumber
    : currentLead.lastUpdated;
  // eslint-disable-next-line react-hooks/purity
  const ageMs = Date.now() - createdAtMs;

  // Stale lead & duplicate phone checks
  const isStaleLead = ageMs > 12 * 60 * 60 * 1000; // > 12 hours

  let duplicateWarning: string | null = null;
  const digitsForCurrent = normalizePhoneDigits(e.phone);
  if (digitsForCurrent.length >= 10) {
    const matching = leads.filter((l) => {
      if (l.id === currentLead.id) return false;
      if (l.isTest) return false;
      if (!l.extracted || !l.extracted.phone) return false;

      const otherDigits = normalizePhoneDigits(l.extracted.phone);
      if (otherDigits !== digitsForCurrent) return false;

      return isToday(l.lastUpdated);
    });

    if (matching.length > 0) {
      if (matching.some((l) => l.sent)) {
        duplicateWarning =
          "This phone number already has a sent lead from earlier today. Make sure you really want another one.";
      } else {
        duplicateWarning =
          "This phone number appears in multiple leads from today. Consider merging or deleting duplicates later.";
      }
    }
  }

  return (
    <div
      data-app-panel="LeadSummary"
      className={`p-8 shadow-lg ${cardSurfaceClass}`}
      data-location-display={locationDisplay}
      data-lead-source-display={leadSourceDisplay}
      data-install-address-display={installAddressDisplay}
    >
      {lastUpdatedDisplay && (
        <p className={`text-sm mb-4 ${cardMutedTextClass}`}>
          Last updated at {lastUpdatedDisplay}. This summary updates as you keep
          talking.
        </p>
      )}

      {leadProvenanceNode ? <div className="mb-4">{leadProvenanceNode}</div> : null}

      {currentLead.origin_location_id && !leadProvenanceNode ? (
        <div className="mb-4 text-xs sm:text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-600 rounded-2xl px-4 py-2">
          <span className="font-semibold text-slate-900 dark:text-slate-100">Origin location: </span>
          {(currentLead.origin_location_name || "").trim() ||
            (currentLead.origin_location_id || "").trim() ||
            "—"}
        </div>
      ) : null}

      {showGhlLocationMismatchHint && (
        <div className="mb-4 text-sm text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-2xl px-4 py-3">
          Your workspace location
          {workspaceLocationLabel ? (
            <>
              {" "}
              (<span className="font-semibold">{workspaceLocationLabel}</span>)
            </>
          ) : null}{" "}
          differs from this lead&apos;s origin. When you send to Go High Level, you can choose which
          location to use.
        </div>
      )}

      {(isStaleLead || duplicateWarning) && (
        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 dark:bg-amber-950/35 dark:border-amber-800 dark:text-amber-200">
          {isStaleLead && (
            <p className="mb-1">
              This lead is over 12 hours old. Make sure you’re not
              accidentally editing an old call when you meant to start a new
              customer.
            </p>
          )}
          {duplicateWarning && <p>{duplicateWarning}</p>}
        </div>
      )}

        <LeadSummaryHeader
          currentLead={currentLead}
          e={e}
          timelineDisplay={timelineDisplay}
          leadStatusClass={leadStatusClass}
          leadStatusLabel={leadStatusLabel}
          leadAgeDisplay={leadAgeDisplay}
          compactView={compactView}
          setCompactView={setCompactView}
          mergeSuggestion={mergeSuggestion}
          setMergeSuggestion={setMergeSuggestion}
          mergeLeadIntoCurrent={mergeLeadIntoCurrent}
          systemTags={systemTags}
          editableTags={editableTags}
          newTagInput={newTagInput}
          setNewTagInput={setNewTagInput}
          updateCurrentLeadField={updateCurrentLeadField}
          addTag={addTag}
        />

        <NotesSection e={e} updateCurrentLeadField={updateCurrentLeadField} />

        {leadTransferSection}

        {submitAsDealSection}

        <LeadSummaryActions
          currentLead={currentLead}
          ghlPayload={ghlPayload}
          validationIssues={validationIssues}
          canSend={canSend}
          isSending={isSending}
          handleSendClick={handleSendClick}
          reextractCurrentLead={reextractCurrentLead}
          devMode={devMode}
        />
    </div>
  );
}
